import {computed, ref, watch} from 'vue';

import type {IUser, IUserCurrent} from '@/api/types/user';
import {ERoleSystem} from '@/api/enums/role';

import {keycloakAPI} from '@/assets/keycloak';

import userController from '@/services/controllers/user';

const useUserHook = () => {
  const userAPIInfo = ref<IUser | undefined>(undefined);

  const getUserInfoIsLoaded = computed(() => !!userAPIInfo.value);

  const getAPIInfo = computed(() => userAPIInfo.value);
  const getInfo = computed<IUserCurrent>(() => ({
    ...keycloakAPI.getUserInfo.value,
    ...getAPIInfo.value,
    tech_roles: getAPIInfo.value?.tech_roles ?? [],
    view_roles: getAPIInfo.value?.view_roles ?? [],
    view_roles_map: getAPIInfo.value?.view_roles_map ?? [],
  }));
  const getId = computed(() => getInfo.value.id);

  const getName = computed(() => getInfo.value.name?.trim() ?? 'guest');

  const getRoles = computed(() => getInfo.value.roles as ERoleSystem[]);
  const isGuest = computed(() => !keycloakAPI.getIsLoggedIn.value);
  const isAdmin = computed(() =>
    getRoles.value?.some((item) => item.toLowerCase() === ERoleSystem.ADMIN),
  );
  const isUser = computed(() =>
    getRoles.value?.some((item) => item.toLowerCase() === ERoleSystem.USER),
  );

  const loadAPIUserInfo = async (payload?: IUser) => {
    userAPIInfo.value =
      payload ?? (getId.value ? await userController.getOneMe() : undefined);
  };

  const hasAccessByRole = (roles?: ERoleSystem[]) => {
    const userRolesSet = new Set(getRoles.value);
    const accessRolesSet = new Set(roles ?? []);

    const rolesIntersection = new Set(
      [...accessRolesSet].filter((item) => userRolesSet.has(item)),
    );

    return !!rolesIntersection.size;
  };

  watch(getId, () => loadAPIUserInfo());

  loadAPIUserInfo();

  return {
    getUserInfoIsLoaded,

    getId,
    getInfo,
    getAPIInfo,
    getName,
    getRoles,

    isAdmin,
    isGuest,
    isUser,

    loadAPIUserInfo,
    hasAccessByRole,

    getIsLoggedIn: keycloakAPI.getIsLoggedIn,
    signIn: keycloakAPI.signIn,
    signOut: keycloakAPI.signOut,
  };
};

const user = useUserHook();

export default user;
